import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserWorkspaces } from './useUserWorkspaces';
import {
  generateWorkspacePreferenceKey,
  getWorkspaceIdFromPreferenceKey,
  getWorkspacePreferenceKey,
} from './utils';
import { Workspace } from './WorkspaceContext';
import { useHistory, useLocation } from 'react-router-dom';
import { WORKSPACES_PAGE_ROUTE } from 'const';

export const useWorkspacesMenu = () => {
  const { t } = useTranslation('workspace');

  const { pathname } = useLocation();
  const { push, replace } = useHistory();

  const { userWorkspaces, loading, setPreference, deletePreference } = useUserWorkspaces();

  const addWorkspace = useCallback(
    (defaults: Partial<Workspace> = {}) => {
      const preferenceKey = defaults.id
        ? getWorkspacePreferenceKey(defaults.id)
        : generateWorkspacePreferenceKey();

      const newWorkspace: Workspace = {
        id: getWorkspaceIdFromPreferenceKey(preferenceKey),
        layout: {},
        title: t('defaultWorkspaceName', { number: userWorkspaces.length + 1 }),
        gridItemData: {},
        ...defaults,
      };
      return setPreference(preferenceKey, newWorkspace).then(() => {
        push(`/${WORKSPACES_PAGE_ROUTE}/${newWorkspace.id}`);
      });
    },
    [t, userWorkspaces.length, setPreference, push]
  );

  const deleteWorkspace = useCallback(
    (workspaceId: string) => {
      return deletePreference(getWorkspacePreferenceKey(workspaceId)).then(() => {
        if (pathname.includes(workspaceId)) {
          replace('/');
        }
      });
    },
    [deletePreference, pathname, replace]
  );

  return {
    userWorkspaces,
    addWorkspace,
    deleteWorkspace,
    loading,
  };
};
